import type { FunctionComponent, ReactNode } from "react";
import React from "react";

import classNames from "classnames";
import styled from "styled-components";

import { colorsPalettes, $robotoFontFamily } from "@similarweb/styles";
import type { ISite } from "@similarweb/ui-components";
import { DotsLoader, Autocomplete } from "@similarweb/ui-components";

import { BLOCK_SITE_STATUS } from "pro-features/components/AutocompleteDigitalMarketing/AutocompleteDigitalMarketingHelper";
import { filterOutIllegalWebsiteItems } from "pro-features/components/AutocompleteWebsites/utils";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    createWebsiteSearchResultItem as defaultCreateWebsiteSearchResultItem,
    getWebsiteResults,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";

const translate = i18nFilter();
const AutocompleteStyled = styled(Autocomplete)`
    width: 100%;
`;
const PlaceholderText = styled.span`
    color: ${colorsPalettes.carbon["300"]};
    font-family: ${$robotoFontFamily};
    font-size: 14px;
    margin-left: 36px;
`;
const BoldPlaceholderText = styled.span`
    font-weight: 500;
`;

interface IAutocompleteWebsitesBaseProps {
    autocompleteProps?: any;
    defaultWebsitePageState?: string;
    defaultWebsitePageStateParams?: any;
    renderOnEmptyQuery?: () => Promise<any> | ReactNode | unknown[];

    onItemClick?(domain: any): void;

    placeholder?: ReactNode;
    createWebsiteSearchResultItem?: (
        item: ISite,
        websitePageStateName?: string,
        stateParams?: any,
        onItemClick?: ((domain: any) => void) | ((item: ISite) => void),
    ) => ReactNode;
    autoCompleteRef?: React.Ref<any>;
    // exclude websites from the search results
    exclude?: string[];
}

const PlaceholderComponent = ({ text }: { text: ReactNode }) => <PlaceholderText>{text}</PlaceholderText>;

export const AutocompleteWebsitesBase: FunctionComponent<IAutocompleteWebsitesBaseProps> = ({
    autocompleteProps,
    defaultWebsitePageState,
    defaultWebsitePageStateParams,
    renderOnEmptyQuery = () => null,
    onItemClick,
    placeholder,
    createWebsiteSearchResultItem = defaultCreateWebsiteSearchResultItem,
    autoCompleteRef,
    exclude = [],
}) => {
    const [isAutocompleteLoading, setAutocompleteLoading] = React.useState<boolean>(false);
    const onAutocompleteGetData = async (query: string) => {
        if (query === "") {
            return renderOnEmptyQuery();
        } else {
            setAutocompleteLoading(true);
            const results = await getWebsiteResults(query);
            setAutocompleteLoading(false);
            return results
                .filter(({ name }) => !exclude.includes(name))
                .filter((res) => (Array.isArray(res.blockStatus) ? !res.blockStatus.includes(BLOCK_SITE_STATUS) : true))
                .filter((res) => filterOutIllegalWebsiteItems(res))
                .map((item) => {
                    return createWebsiteSearchResultItem(
                        item,
                        // @ts-ignore FIXME: strict migration - 'string | undefined' is not assignable to 'string' — interface allows undefined but default impl requires string
                        defaultWebsitePageState,
                        defaultWebsitePageStateParams,
                        onItemClick,
                    );
                });
        }
    };

    const placeholderContent =
        placeholder !== undefined ? (
            placeholder
        ) : (
            <>
                {translate("marketintelligence.companyresearch.websites.home.searchText")}{" "}
                {translate("marketintelligence.companyresearch.websites.home.searchItem")}
            </>
        );

    return (
        <AutocompleteStyled
            className={classNames("AutocompleteWebsitesBase", "autoCompletePrimaryDomain")}
            ref={autoCompleteRef}
            placeholder={<PlaceholderComponent text={placeholderContent} />}
            loadingComponent={isAutocompleteLoading ? <DotsLoader /> : <i />}
            floating={true}
            debounce={250}
            getListItems={onAutocompleteGetData}
            isLoading={isAutocompleteLoading}
            maxResults={8}
            {...autocompleteProps}
        />
    );
};
